import { USER_ROLES } from '../enums'
import { UserRole } from '@prisma/client'

/**
 * The role groups both sides of the wire are written against: one grouping
 * decides what a route admits and what the rail offers, so a screen cannot be
 * hidden by the navigation while the API still serves it.
 *
 * Ordering follows the enum: CUSTOMER → ARCHITECT → SALES → PROJECT_MANAGER →
 * SITE_MANAGER → ADMIN → ROOT.
 */

/** The catalogue, the configurator, the accounts and the lists they are built
 *  from. */
export const ADMIN_ROLES = [UserRole.ADMIN, UserRole.ROOT] as const

/** The running system: an admin reads it, root changes it. */
export const ROOT_ROLES = [UserRole.ROOT] as const

/**
 * Every role that works inside the app: the projects, their planning, their
 * documents and the catalogue they are built from.
 *
 * Whether one of them may *change* what it reaches is a second question this
 * list does not answer — `AbilityFactory` answers it per subject, and
 * `@ProjectScope()` per record.
 */
export const INTERNAL_ROLES = [
  UserRole.ARCHITECT,
  UserRole.SALES,
  UserRole.PROJECT_MANAGER,
  UserRole.SITE_MANAGER,
  UserRole.ADMIN,
  UserRole.ROOT,
] as const

/**
 * The roles that advise a customer: matching and search mandates, the objects
 * they are matched against, HubSpot, and the customer's own screens watched
 * from inside.
 *
 * ARCHITECT is absent, and absent for reading too: matching is the one area
 * closed to it whole, and an object is where a match lands.
 */
export const ADVISOR_ROLES = [
  UserRole.SALES,
  UserRole.ADMIN,
  UserRole.ROOT,
] as const

/**
 * The catalogue and the media library: measures, products, formulas, folders,
 * images.
 *
 * What an architect owns. SALES reads parts of it for the planning area — one
 * opened route at a time, held to that by `planning-route-roles.spec.ts` — and
 * changes none of it. What configures the app rather than filling it — the
 * configurator, the classification lists, the element types — stays
 * `ADMIN_ROLES`.
 */
export const CATALOGUE_ROLES = [
  UserRole.ARCHITECT,
  UserRole.ADMIN,
  UserRole.ROOT,
] as const

/**
 * Who may look at the catalogue without changing any of it.
 *
 * The roles that run a project read what it is built from — which measure a
 * position came from, what a product costs — and write none of it. The media
 * library is not in it: a picture is the catalogue's own product and the
 * roles here have no use for the library that files it.
 *
 * `catalogue-route-roles.spec.ts` holds every read of the catalogue's API to
 * this list and every write to the one above, so a route added to the
 * controller is placed by that spec rather than by whoever writes it.
 */
export const CATALOGUE_READ_ROLES = [
  UserRole.ARCHITECT,
  UserRole.PROJECT_MANAGER,
  UserRole.SITE_MANAGER,
  UserRole.ADMIN,
  UserRole.ROOT,
] as const

/**
 * A project's money: its contracts, invoices, approvals and margin. The
 * administration's alone, reading included: the figures are the margin,
 * and the project's own architect is one of the parties paid there.
 */
export const FINANCE_ROLES = ADMIN_ROLES

/**
 * The roles a project is scoped to the people it names.
 *
 * They read every project and change the ones they are named on or were given;
 * every other role either works on all of them or on none. Read by the guard
 * that refuses the request and by the screens that stop offering the change.
 *
 * One role per post of `PROJECT_POSTS`, which is what a post is: the role whose
 * work on a project is that post.
 */
export const PROJECT_SCOPED_ROLES = [
  UserRole.SALES,
  UserRole.PROJECT_MANAGER,
  UserRole.SITE_MANAGER,
] as const

/**
 * The accounts an admin runs: every one that is not itself an administrator.
 *
 * Who an admin may create, whose password they may reset, and which roles they
 * may move somebody between — `UsersController` checks a role change against
 * the CREATE rule for the new role, so one list answers all of it.
 *
 * ADMIN and ROOT are absent, so the role that administers the system is only
 * ever handed out by root.
 */
export const ADMIN_MANAGED_ROLES = [
  UserRole.CUSTOMER,
  UserRole.ARCHITECT,
  UserRole.SALES,
  UserRole.PROJECT_MANAGER,
  UserRole.SITE_MANAGER,
] as const

/**
 * Whether a route's role list admits an account: a route naming no roles
 * admits every signed-in account, one naming some admits whoever holds any
 * of them. `RolesGuard` decides requests by it, and the MCP server lists
 * routes by it.
 */
export function rolesAdmit(
  required: readonly UserRole[] | null | undefined,
  held: readonly UserRole[] | null | undefined,
): boolean {
  return !required || required.length === 0 || holdsAny(held, required)
}

/**
 * The roles an actor may hand out, by the role they hold.
 *
 * `AbilityFactory` builds its `User` rules from this and the users screen
 * offers exactly these in the role field, so the form cannot offer a role the
 * server then refuses.
 */
export const ASSIGNABLE_ROLES = {
  [UserRole.CUSTOMER]: [],
  [UserRole.ARCHITECT]: [],
  // A mandate without an account is turned into one from the matching sidebar.
  [UserRole.SALES]: [UserRole.CUSTOMER],
  [UserRole.PROJECT_MANAGER]: [],
  [UserRole.SITE_MANAGER]: [],
  [UserRole.ADMIN]: ADMIN_MANAGED_ROLES,
  [UserRole.ROOT]: USER_ROLES,
} as const satisfies Record<UserRole, readonly UserRole[]>

/**
 * The roles an account holds only on its own.
 *
 * A customer is outside the company, and an administrator already reaches
 * everything a second role would add. The working roles combine: a user
 * holding several has what each of them grants.
 */
export const EXCLUSIVE_ROLES = [
  UserRole.CUSTOMER,
  UserRole.ADMIN,
  UserRole.ROOT,
] as const

/** Whether a role is one an account holds only on its own. */
export const isExclusiveRole = (role: UserRole): boolean =>
  (EXCLUSIVE_ROLES as readonly UserRole[]).includes(role)

/** The roles that combine, in the enum's order. */
export const COMBINABLE_ROLES = USER_ROLES.filter(
  role => !isExclusiveRole(role),
)

/** What an account holds where nobody named its roles. */
export const DEFAULT_USER_ROLES: readonly UserRole[] = [UserRole.CUSTOMER]

/** What is wrong with a set of roles, or null for one an account may hold. */
export type RoleSetProblem = 'ROLES_EMPTY' | 'ROLE_NOT_COMBINABLE'

/**
 * Checks a set of roles against the rule: at least one, and an exclusive role
 * alone. The database holds the table to the same rule with a CHECK
 * constraint, so this is what turns a refused write into a sentence.
 */
export function roleSetProblem(
  roles: readonly UserRole[],
): RoleSetProblem | null {
  const distinct = new Set(roles)
  if (distinct.size === 0) return 'ROLES_EMPTY'
  if (distinct.size > 1 && [...distinct].some(isExclusiveRole)) {
    return 'ROLE_NOT_COMBINABLE'
  }
  return null
}

/** Duplicates dropped and the enum's order restored, so two equal sets compare
 *  and render alike. */
export function normalizeRoles(roles: readonly UserRole[]): UserRole[] {
  return USER_ROLES.filter(role => roles.includes(role))
}

/** Whether any role held is one of `allowed`. */
export function holdsAny(
  held: readonly UserRole[] | null | undefined,
  allowed: readonly UserRole[],
): boolean {
  return !!held?.some(role => allowed.includes(role))
}

/**
 * The roles an actor may hand out: what each role they hold may, together.
 *
 * `ASSIGNABLE_ROLES` answers per role; an account holding two gets both
 * answers.
 */
export function assignableRoles(actor: readonly UserRole[]): UserRole[] {
  return normalizeRoles(actor.flatMap(role => ASSIGNABLE_ROLES[role]))
}

/** Whether an actor may give an account exactly these roles. */
export function canAssignRoles(
  actor: readonly UserRole[],
  target: readonly UserRole[],
): boolean {
  const allowed = assignableRoles(actor)
  return target.every(role => allowed.includes(role))
}
